import { PURCHASE_AMENDMENT_KIND } from './purchaseAmendmentKind';

function pickLineValues(kind, line) {
  if (!line) {
    return null;
  }
  if (kind === PURCHASE_AMENDMENT_KIND.MISC_PURCHASE_ORDER) {
    return {
      qty: line.qty ?? null,
      cancelQty: null,
      unitPrice: line.unitPrice ?? null,
      amount: line.amount ?? null,
      remark: line.remark ?? null,
    };
  }
  // 物料採購快照為 PurchaseOrderMutationLine（orderQuantity／cancelQuantity）
  return {
    qty: line.orderQuantity ?? line.qtySummary?.orderQty ?? line.orderQty ?? null,
    cancelQty: line.cancelQuantity ?? line.qtySummary?.cancelQty ?? line.cancelQty ?? null,
    unitPrice: line.unitPrice ?? null,
    amount: line.subtotalAmount ?? line.amount ?? null,
    remark: line.remark ?? null,
  };
}

function lineKey(kind, line, index) {
  if (kind === PURCHASE_AMENDMENT_KIND.MISC_PURCHASE_ORDER) {
    return line?.lineId ? `line:${line.lineId}` : `new:${index}`;
  }
  return `mtr:${line?.materialId ?? line?.mtrId ?? index}`;
}

function buildMtrLookup(orderLines) {
  const map = new Map();
  (orderLines ?? []).forEach((line) => {
    const id = line?.mtr?.id ?? line?.mtrId;
    if (id) {
      map.set(id, { name: line.mtr?.name ?? '', code: line.mtr?.mtrNo ?? '' });
    }
  });
  return map;
}

function lineLabel(kind, line, mtrLookup) {
  if (kind === PURCHASE_AMENDMENT_KIND.MISC_PURCHASE_ORDER) {
    return { name: line?.itemName ?? '', code: line?.specification ?? '' };
  }
  const id = line?.materialId ?? line?.mtrId;
  const known = mtrLookup.get(id);
  return {
    mtrId: id ?? null,
    name: known?.name || line?.materialName || '',
    code: known?.code || line?.materialNo || '',
  };
}

function isSameValues(left, right) {
  if (!left || !right) {
    return false;
  }
  return ['qty', 'cancelQty', 'unitPrice', 'remark'].every(
    (key) => String(left[key] ?? '') === String(right[key] ?? ''),
  );
}

/**
 * 異動 before／after 明細 → 對照列（依物料或 lineId 配對）。
 * `change`：`ADDED`｜`REMOVED`｜`CHANGED`｜`UNCHANGED`。
 */
export function buildPurchaseAmendmentLineDiff(kind, before, after, orderLines) {
  const mtrLookup = buildMtrLookup(orderLines);
  const rows = new Map();

  (before?.lines ?? []).forEach((line, index) => {
    const key = lineKey(kind, line, index);
    rows.set(key, {
      key,
      label: lineLabel(kind, line, mtrLookup),
      before: pickLineValues(kind, line),
      after: null,
    });
  });

  (after?.lines ?? []).forEach((line, index) => {
    const key = lineKey(kind, line, `after-${index}`);
    const existing = rows.get(key);
    if (existing) {
      existing.after = pickLineValues(kind, line);
      return;
    }
    rows.set(key, {
      key,
      label: lineLabel(kind, line, mtrLookup),
      before: null,
      after: pickLineValues(kind, line),
    });
  });

  return [...rows.values()].map((row) => {
    let change = 'CHANGED';
    if (!row.before) {
      change = 'ADDED';
    } else if (!row.after) {
      change = 'REMOVED';
    } else if (isSameValues(row.before, row.after)) {
      change = 'UNCHANGED';
    }
    return { ...row, change };
  });
}
